import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { LinePayload } from '../common/payload.types';

import { apiResourceSchema } from '../common/schemas';
import { fetchJson } from '../fetch';
import { QueryKeys } from '../keys';
import { draftQuotationSchema } from './schemas';
import { quotationQueryOptions } from './useQuotation';

export type Payload = {
  brandId: number | null;
  clientContactId: number | null;
  clientId: number | null;
  coEntryNumber: string | null;
  conditionId: number | null;
  customAgreementId: number | null;
  customConditionId: number | null;
  description: string | null;
  expirationDate: string | null;
  introduction: string | null;
  mail: string | null;
  quotationLines: LinePayload[];
  specialConditions: string | null;
  title: string | null;
};

export const useUpdateDraftQuotation = (id: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: Payload) => {
      const { data: quotation } = await fetchJson(`/quotations/draft/${id}`, {
        method: 'PATCH',
        schema: apiResourceSchema(draftQuotationSchema),
        body,
      });

      return quotation;
    },
    onSuccess: (quotation) => {
      // An autosave that is still in flight when the quotation gets submitted may resolve after the submit has cached
      // the submitted quotation. Writing the draft back over it would resurrect a status the server no longer reports.
      queryClient.setQueryData(quotationQueryOptions(quotation.id).queryKey, (cachedQuotation) =>
        cachedQuotation?.status === 'Draft' ? quotation : cachedQuotation,
      );

      queryClient.invalidateQueries({ queryKey: QueryKeys.quotations.index.root });
    },
  });
};
